import {
  AuthTokenErrorType,
  AuthTokenSource,
  IAuthTokenData,
  IAuthTokenError,
  IAuthTokenStore,
  ISourceTokenDebugger,
  TokenDependencies,
} from './authTokenTypes';
import { action, makeAutoObservable } from 'mobx';
import { AuthTokenHelper } from './authTokenHelper';
import { IWallClock } from '../../utils/time/IWallClock';
import { DefaultWallClock } from '../../utils/time/DefaultWallClock';
import { SourceTokenDebugger } from './TokenDebugger';
import { WriteableServerCookies } from '../../utils/serverCookies';
import { DateTime } from 'luxon';
import { isTokenError } from './isTokenError';
import { BW_SESSIONID_COOKIE_NAME } from './bruntwoodSessionStore';
import {
  NEXUDUS_TOKEN_COOKIE_NAME,
  NEXUDUS_TOKEN_EXPIRY_NAME,
} from '@portal/env/stores/authTokens/consts';
import { NexudusTokenRefresher } from '@portal/env/stores/authTokens/nexudusTokenRefresher';

export class NexudusTokenStore implements IAuthTokenStore {
  public readonly source = 'nexudusToken';
  public readonly meta = {};

  public readonly isRequired = true;

  public readonly subscriber = null;
  public readonly usedCookieNames: string[] = [
    NEXUDUS_TOKEN_COOKIE_NAME,
    NEXUDUS_TOKEN_EXPIRY_NAME,
  ];

  /**
   * We can get a new one of these either from an old one or from the bruntwood session token
   */
  public readonly dependencies: AuthTokenSource[] = [
    'bruntwoodSession',
    'bruntwoodToken',
  ];

  private _value: string | null;

  private readonly helper: AuthTokenHelper;
  constructor(
    value: string | null,
    expiryDate: DateTime | null,
    private readonly wallClock: IWallClock = DefaultWallClock.instance,
    private readonly tokenDebugger: ISourceTokenDebugger = new SourceTokenDebugger(
      this.source,
    ),
    private readonly refresher: NexudusTokenRefresher = new NexudusTokenRefresher(
      this.tokenDebugger,
    ),
  ) {
    this._value = value;
    this.helper = new AuthTokenHelper(
      {
        wallClock: this.wallClock,
      },
      value
        ? {
            value,
            expiryDate: expiryDate,
            source: this.source,
          }
        : undefined,
    );
    makeAutoObservable(this);
  }

  get value(): string | null {
    return this._value;
  }

  get isReady(): boolean {
    return (
      !!this._value &&
      !this.helper.isInvalidatedByFailures() &&
      !this.helper.isExpired()
    );
  }

  get canAttemptAgain(): boolean {
    return this.helper.hasRemainingAttempts();
  }
  get expiryDate(): DateTime | null {
    return this.helper.expiryDate;
  }
  get isExpired(): boolean {
    return this.helper.isExpired();
  }
  get shouldRefresh(): boolean {
    return this.helper.needsRefresh();
  }
  get timeoutExpiry(): DateTime | null {
    return this.helper.backoffExpiry;
  }

  saveCookies(writeableCookies: WriteableServerCookies): void {
    if (this._value) {
      writeableCookies.set(NEXUDUS_TOKEN_COOKIE_NAME, this._value);
    } else {
      writeableCookies.clear(NEXUDUS_TOKEN_COOKIE_NAME);
    }

    const expiryDateISO = this.helper.expiryDate?.toISO();
    if (this.helper.expiryDate && expiryDateISO) {
      writeableCookies.set(NEXUDUS_TOKEN_EXPIRY_NAME, expiryDateISO);
      writeableCookies.setExpiry(
        NEXUDUS_TOKEN_COOKIE_NAME,
        this.helper.expiryDate,
      );
      writeableCookies.setExpiry(
        NEXUDUS_TOKEN_EXPIRY_NAME,
        this.helper.expiryDate,
      );
    } else {
      writeableCookies.clear(NEXUDUS_TOKEN_EXPIRY_NAME);
    }
  }

  public get cookies(): Record<string, string | null> {
    return {
      // Don't need to separately store the expiry date as it's in the token
      [NEXUDUS_TOKEN_COOKIE_NAME]: this._value,
      [NEXUDUS_TOKEN_EXPIRY_NAME]: this.helper.expiryDate?.toISO() || null,
    };
  }

  async checkResponse(
    response: Response,
    body,
  ): Promise<IAuthTokenError | null> {
    if (response.status === 401) {
      // Generally requests to nexudus will return 401 if the token is invalid
      this.helper.recordFailedRequest();
      this.tokenDebugger.addEvent({
        action: 'fetchFailed',
        message: `Failed to fetch from nexudus ${body}`,
      });
      return {
        source: this.source,
        type: AuthTokenErrorType.UNAUTHORIZED,
        message: 'Unauthorized',
      };
    }
    return null;
  }

  @action
  update(data: IAuthTokenData): void {
    this.tokenDebugger.addEvent({
      action: 'updated',
      message: `Updated with new token, expires at ${data.expiryDate?.toISO()}`,
    });
    this.helper.recordNewToken(data.expiryDate);
    this._value = data.value;
  }

  async updateWith(
    dependencies: TokenDependencies,
  ): Promise<IAuthTokenError | null> {
    // If we've got a valid bruntwood session token, we can use that to get a new bruntwood token
    let headers = {};

    if (dependencies.bruntwoodToken) {
      headers = {
        Authorization: `Bearer ${dependencies.bruntwoodToken.value}`,
      };
    } else if (dependencies.bruntwoodSession) {
      headers = {
        Cookie: `${BW_SESSIONID_COOKIE_NAME}=${dependencies.bruntwoodSession.value}`,
      };
    }
    if (headers) {
      const result = await this.refresher.tryFetchToken(headers);
      if (isTokenError(result)) {
        return result;
      } else {
        this.update({
          value: result.access_token,
          source: this.source,
          expiryDate: this.wallClock.now().plus({ seconds: result.expires_in }),
        });
        return null;
      }
    }

    return {
      source: this.source,
      type: AuthTokenErrorType.NO_TOKEN,
      message: 'Could not get dependencies for nexudus token',
    };
  }
}
